import Link from '@mui/material/Link';
import Masonry from '@mui/lab/Masonry';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import TextField from '@mui/material/TextField';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import { alpha, styled } from '@mui/material/styles';
import Stack, { StackProps } from '@mui/material/Stack';
import InputAdornment from '@mui/material/InputAdornment';
import Button, { buttonClasses } from '@mui/material/Button';
import { paths } from '../../Routes/paths';

import { _socials } from '../../_mock';
import Logo from '../../Components/logo';
import Iconify from '../../Components/iconify';
import { usePathname } from '../../Routes/hooks';
import { useBoolean } from '../../Hooks/use-boolean';
import { RouterLink } from '../../Routes/components';
import { useResponsive } from '../../Hooks/use-responsive';

import { NavListProps, NavItemBaseProps } from './nav/types';
import { navConfig } from './config-navigation';
import Parse from "parse";
import { useState } from "react";
import { useSnackbar } from "notistack";

// ----------------------------------------------------------------------

const StyledAppStoreButton = styled(Button)(({ theme }) => ({
  flexShrink: 0,
  padding: '5px 12px',
  color: theme.palette.common.white,
  border: `solid 1px ${alpha(theme.palette.common.black, 0.24)}`,
  background: `linear-gradient(180deg, ${theme.palette.grey[900]} 0%, ${theme.palette.common.black} 100%)`,
  [`& .${buttonClasses.startIcon}`]: {
    marginLeft: 0,
  },
}));

// ----------------------------------------------------------------------

export default function Footer() {
  const mdUp = useResponsive('up', 'md');

  const pathname = usePathname();

  const mobileList = navConfig.find((i) => i.title === 'Pages')?.children || [];
  const desktopList = navConfig.filter((i) => i.title === 'Explore' || i.title === 'Company') || [];

  const renderLists = mdUp ? navConfig : mobileList;

  const [prospectEmail, setProspectEmail] = useState("");
  const { enqueueSnackbar } = useSnackbar();

  const isHome = pathname === '/';

  const handleNewProspect = async (email) => {
    if (validateEmail(email)) {
      Parse.Cloud.run("logNewProspect", {
        email: email,
        fromPage: 'footer'
      }).then(async function (response) {
        console.log(response);
      });

      enqueueSnackbar("Success! Subscribed.", {
        variant: "success",
      });

    } else {
      enqueueSnackbar("Oops! This does not appear to be a valid email.", {
        variant: "error",
      });
    }
  }

  function validateEmail(input) {
    var validRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (input.match(validRegex)) {
      return true;

    } else {
      return false;
    }
  }

  const simpleFooter = (
    <Container sx={{ py: 8, textAlign: 'center' }}>
      <Logo single />

      <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
        © 2025. All rights reserved
      </Typography>
    </Container>
  );

  const mainFooter = (
    <>
      <Divider />

      <Container
        sx={{
          overflow: 'hidden',
          py: { xs: 7, md: 7 },
        }}
      >
        <Grid container spacing={3} justifyContent={{ md: 'space-between' }}>
          <Grid xs={12} md={4}>
            <Stack spacing={{ xs: 3, md: 5 }}>
              <Stack alignItems="flex-start" spacing={3}>
                <Logo />

                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  We give students a one-stop-shop for the college recruitment process, while providing colleges with a fully 
                  managed service by the OptN team, utilizing next-gen solutions to effectively engage the modern student.
                </Typography>
              </Stack>

              <Stack spacing={2}>
                <Stack spacing={1}>
                  <Typography variant="h6">Let’s stay in touch</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Subscribe to our newsletter to receive latest articles to your inbox.
                  </Typography>
                </Stack>

                <TextField
                  hiddenLabel
                  variant="standard"
                  placeholder="Email Address"
                  onChange={(e) => setProspectEmail(e.target.value)}
                  sx={{
                    width: 365,
                    input: { color: '#000', marginLeft: 3, marginTop: 1.3 },
                    backgroundColor: "#f5f5fa",
                    borderRadius: 20,
                    height: 50,
                  }}
                  InputProps={{
                    disableUnderline: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button
                          variant="contained"
                          color="inherit"
                          size="large"
                          onClick={() => handleNewProspect(prospectEmail)}
                          sx={{
                            borderRadius: 12,
                            mr: 0.5,
                            mt: 1.3,
                            height: 43,
                            fontSize: 12
                          }}>
                          SUBSCRIBE
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>

              {/* <Stack spacing={2}>
                <Typography variant="h6">Social</Typography>
                <Stack direction="row" alignItems="center">
                  {_socials.map((social) => (
                    <IconButton key={social.value} color="primary">
                      <Iconify icon={social.icon} />
                    </IconButton>
                  ))}
                </Stack>
              </Stack> */}

              {/* <Stack spacing={2}>
                <Typography variant="h6">Apps</Typography>
                <AppStoreButton />
              </Stack> */}
            </Stack>
          </Grid>

          <Grid xs={12} md={6}>
            {mdUp ? (
              <Masonry columns={4} spacing={2} defaultColumns={4} defaultSpacing={2}>
                {desktopList.map((list) => (
                  <ListDesktop key={list.title} list={list} />
                ))}
              </Masonry>
            ) : (
              <Stack spacing={1.5}>
                {renderLists.map((list) => (
                  <ListMobile key={list.title} list={list} />
                ))}
              </Stack>
            )}
          </Grid>
        </Grid>
      </Container>

      <Divider />

      <Container>
        <Stack
          spacing={2.5}
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          sx={{ py: 3, textAlign: 'center' }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            © 2025. All rights reserved
          </Typography>

          <Stack direction="row" spacing={3} justifyContent="center">
            <Link
              variant="caption"
              sx={{ color: 'text.secondary' }}
              component={RouterLink}
              href={paths.privacy}
            >
              Privacy Policy
            </Link>

            <Link
              variant="caption"
              sx={{ color: 'text.secondary' }}
              component={RouterLink}
              href={paths.terms}
            >
              Terms of Service
            </Link>
          </Stack>
        </Stack>
      </Container>
    </>
  );

  return <footer>{isHome ? mainFooter : mainFooter}</footer>;
}

// ----------------------------------------------------------------------

export function ListDesktop({ list }: { list: NavItemBaseProps }) {
  const pathname = usePathname();

  return (
    <Stack spacing={1.5} alignItems="flex-start">
      <Typography variant="subtitle2">{list.title}</Typography>

      {list.children?.map((link) => {
        const active = pathname === link.path || pathname === `${link.path}/`;

        return (
          <Link
            component={RouterLink}
            key={link.title}
            href={link.path}
            variant="caption"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'text.primary',
              },
              ...(active && {
                color: 'text.primary',
                fontWeight: 'fontWeightSemiBold',
              }),
            }}
          >
            {link.title}
          </Link>
        );
      })}
    </Stack>
  );
}

// ----------------------------------------------------------------------

export function ListMobile({ list }: { list: NavListProps }) {
  const pathname = usePathname();

  const listExpand = useBoolean();

  return (
    <Stack spacing={1.5} alignItems="flex-start">
      <Typography
        variant="subtitle2"
        onClick={listExpand.onToggle}
        sx={{
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        {list.title}
        <Iconify
          width={16}
          icon={listExpand.value ? 'carbon:chevron-down' : 'carbon:chevron-right'}
          sx={{ ml: 0.5 }}
        />
      </Typography>

      <Collapse in={listExpand.value} unmountOnExit sx={{ width: 1 }}>
        <Stack spacing={1.5} alignItems="flex-start">
          {/* {list.items?.map((link) => (
            <Link
              component={RouterLink}
              key={link.title}
              href={link.path}
              variant="caption"
              sx={{
                color: 'text.secondary',
                '&:hover': {
                  color: 'text.primary',
                },
                ...(pathname === `${link.path}/` && {
                  color: 'text.primary',
                  fontWeight: 'fontWeightSemiBold',
                }),
              }}
            >
              {link.title}
            </Link>
          ))} */}
        </Stack>
      </Collapse>
    </Stack>
  );
}

// ----------------------------------------------------------------------

function AppStoreButton({ ...other }: StackProps) {
  return (
    <Stack direction="row" flexWrap="wrap" spacing={2} {...other}>
      <StyledAppStoreButton startIcon={<Iconify icon="ri:apple-fill" width={28} />}>
        <Stack alignItems="flex-start">
          <Typography variant="caption" sx={{ opacity: 0.72 }}>
            Download on the
          </Typography>

          <Typography variant="h6" sx={{ mt: -0.5 }}>
            Apple Store
          </Typography>
        </Stack>
      </StyledAppStoreButton>

      {/* <StyledAppStoreButton startIcon={<Iconify icon="logos:google-play-icon" width={28} />}>
        <Stack alignItems="flex-start">
          <Typography variant="caption" sx={{ opacity: 0.72 }}>
            Download from
          </Typography>

          <Typography variant="h6" sx={{ mt: -0.5 }}>
            Google Play
          </Typography>
        </Stack>
      </StyledAppStoreButton> */}
    </Stack>
  );
}
